import { UseQueryResult } from '@tanstack/react-query';
import { useAPIQuery } from 'hooks/useAPIQuery';
import { useEffect } from 'react';
import { useRootDispatch, useRootState } from 'store/StateProvider';
import { updateSourcesResults } from 'store/slices/actions';
import { updateCalculatedData } from 'store/slices/calculatedData/calculatedDataActions';
import { CalculatedData, CalculatedDataResponse } from 'types';
import { mergeDailyOrMonthlyData } from 'views/ResultsTabs/Summary/BackupTable/helpers';
import { SIZER_V2_BASE_URL } from 'views/constants';

const defaultWorkloadSummary = (workloads: any) =>
    ['ltr', 'noLtr', 'dtc', 'allSources'].reduce(
        (acc, key) => ({
            ...acc,
            [key]: {
                dailyData: Array(workloads[key]?.dailyData.length).fill({
                    totalDaily: 0,
                    targetTbDaily: 0,
                    total: 0,
                    target: 0,
                    creditsMonthly: 0,
                    cumulatedCredits: 0,
                }),
                monthlyData: Array(workloads[key]?.monthlyData.length).fill({
                    totalDaily: 0,
                    targetTbDaily: 0,
                    total: 0,
                    target: 0,
                    creditsMonthly: 0,
                    cumulatedCredits: 0,
                }),
            },
        }),
        {},
    );

const useGetResult = (enabled = false) => {
    const {
        deploymentConfig: {
            directorySvcResponse: { endpoint, protocol },
        },
        sourcesData: {
            customerDetails: { sizingId, selectedProductCustomerId },
        },
        deploymentConfig: { envData },
    } = useRootState();
    const dispatch = useRootDispatch();

    const detailsUrl = `${SIZER_V2_BASE_URL}/sizing/${sizingId}/detail`;
    const SummaryUrl = `${SIZER_V2_BASE_URL}/sizing/${sizingId}/summary`;
    const method = 'GET';

    const detailsQuery: UseQueryResult<
        {
            result: {
                results: { cloudNativeWorkloads: CalculatedData; hybridWorkloads: CalculatedData } | CalculatedData;
            };
            statusCode: number;
            success: boolean;
        },
        Error
    > = useAPIQuery({
        key: ['resultDetails', sizingId],
        method,
        url: detailsUrl,
        baseUrl: protocol ? `${protocol}://${endpoint}` : envData.MAINLINE_DEP0_API_GATEWAY_URL,
        enabled,
        customHeaders: {
            productID: 12289,
            ...(selectedProductCustomerId ? { productCustomerID: selectedProductCustomerId } : {}),
        },
        options: {
            staleTime: 24 * 60 * 60 * 1000, // 24 hour stale time to avoid refetching the same API
        },
    });
    const summaryQuery: UseQueryResult<{ result: {}; statusCode: number; success: boolean }, Error> = useAPIQuery({
        key: ['resultSummary', sizingId],
        method,
        url: SummaryUrl,
        baseUrl: protocol ? `${protocol}://${endpoint}` : envData.MAINLINE_DEP0_API_GATEWAY_URL,
        enabled,
        customHeaders: {
            productID: 12289,
            ...(selectedProductCustomerId ? { productCustomerID: selectedProductCustomerId } : {}),
        },
        options: {
            staleTime: 24 * 60 * 60 * 1000, // 24 hour stale time to avoid refetching the same API
        },
    });

    useEffect(() => {
        if (summaryQuery?.data?.success && !summaryQuery.isError && enabled) {
            const result = summaryQuery.data.result as CalculatedDataResponse;
            const { hybridWorkloads, cloudNativeWorkloads } = result.results;
            const { results } = summaryQuery.data.result as CalculatedDataResponse;
            if (!hybridWorkloads) {
                results.hybridWorkloads = defaultWorkloadSummary(cloudNativeWorkloads);
            }
            if (!cloudNativeWorkloads) {
                results.cloudNativeWorkloads = defaultWorkloadSummary(hybridWorkloads);
            }
            results.combinedWorkloads = ['ltr', 'noLtr', 'dtc', 'allSources'].reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: {
                        dailyData: mergeDailyOrMonthlyData(
                            results.hybridWorkloads[key]?.dailyData || [],
                            results.cloudNativeWorkloads[key]?.dailyData || [],
                        ),
                        monthlyData: mergeDailyOrMonthlyData(
                            results.hybridWorkloads[key]?.monthlyData || [],
                            results.cloudNativeWorkloads[key]?.monthlyData || [],
                        ),
                    },
                }),
                {},
            );

            dispatch(updateCalculatedData({ ...summaryQuery.data.result, results } as CalculatedDataResponse));
        }
    }, [summaryQuery.data, summaryQuery.isError, enabled]);
    useEffect(() => {
        if (detailsQuery?.data?.success && !detailsQuery.isError && enabled) {
            detailsQuery.data.result = {
                ...detailsQuery.data.result,
                results: {
                    ...(detailsQuery.data.result.results as any)?.cloudNativeWorkloads,
                    ...(detailsQuery.data.result.results as any)?.hybridWorkloads,
                },
            };
            dispatch(updateSourcesResults(detailsQuery.data.result as any));
        }
    }, [detailsQuery.data, detailsQuery.isError, enabled]);
    return detailsQuery.isFetching || summaryQuery.isFetching;
};

export default useGetResult;
